import { each, isEmpty } from 'lodash'

import {
  globalApi,
  listenFirebase,
  setFirebase,
  updateFirebase,
  removeFirebase,
  getFirebase,
} from 'modules/App/globalApi'

import {
  prepDraftProgram,
  cleanDraftProgramForPublishing,
  cleanWorkout,
  prepWorkout,
  getProgramWorkouts,
  getWorkoutsByIds,
  getDuplicatedProgramWkts,
  getDuplicatedProgram,
  cleanMultiWktsForPublishing,
  getDuplicatedProgramMultiWkts,
} from './utils/programUtils'

import { removeProgramInProgramGroups, deleteDraftWktsOfProgram } from './utils/removeProgram'

import {
  getNewEmptyWeek,
  getNewWorkout,
  getNewRestDay,
  getNewPart,
  getNewProgram,
  getNewEmptyWkt,
  getNewRestPart,
  changeSavedPartIds,
  getNewVideoDay,
} from 'modules/Programs/programModels'

import { initDraftProgram } from './utils/initPrograms'
import { calculateWorkoutEstimate, isTooShortToShow, getInflationEnabled } from 'common/utils/wktDurationUtils'
import { getTimeDurationString } from 'common/utils/timeDuration'
import { takePendingWorkoutText } from './utils/pendingWorkoutText'
import { createUID } from 'common/utils/createUID'

import { copiedWeeksCleared, draftWorkoutRetrieved, draftWorkoutsDeleted } from 'modules/Programs/programSlice'
import { removeItemsInCollections } from 'modules/Collections/collectionUtils'
import { updateTabRowsOnDelete } from 'modules/Layout/utils/layoutUtils'
import { updateItemContentTags } from 'modules/ContentTags/contentTagUtils'
import { updateManyWorkoutContentTags } from './utils/contentTagUtils'

export const programApi = globalApi.injectEndpoints({
  endpoints: (build) => ({
    listenDraftProgram: build.query({
      queryFn: () => {
        return { data: { isFiller: true } }
      },
      onCacheEntryAdded: async ({ orgId, programId }, api) => {
        const path = `drafts/${orgId}/programs/${programId}`
        listenFirebase(path, api, async (draftProg) => {
          if (!draftProg) {
            api.updateCachedData(() => ({ shouldRedirect: true }))
          } else if (!draftProg.workouts) {
            //notify component to initialize program
            api.updateCachedData(() => ({ shouldInitDraft: true }))
          } else {
            const { wktsByWeek, numWeeks, wktIdToMultiWkts, hasWorkouts, wktIdsToDayIdcs, wktIdsSorted } =
              prepDraftProgram(draftProg)

            const newCache = {
              program: draftProg,
              wktsByWeek,
              numWeeks,
              wktIdToMultiWkts,
              hasWorkouts,
              wktIdsToDayIdcs,
              wktIdsSorted,
            }

            api.updateCachedData(() => newCache)
          }
        })
      },
    }),
    initDraftProgram: build.mutation({
      queryFn: async ({ orgId, programId }) => {
        console.log('API initDraftProgram')
        const { status, error } = await initDraftProgram({ programId, orgId })
        return { status, error }
      },
    }),
    listenOrgProgramsList: build.query({
      queryFn: () => ({ data: { stillLoading: true } }),
      onCacheEntryAdded: async ({ orgId }, api) => {
        listenFirebase(`drafts/${orgId}/programs`, api, (programs) => {
          if (!programs) {
            api.updateCachedData(() => ({}))
          } else {
            //PATCH: for some reason, programs from a diff org sometimes show
            //when we switch orgs, causing draft program data to be
            //written into another org's drafts
            const programsWithoutWrongOrgId = {}
            each(programs, (program, programId) => {
              //legacy programs (pre-dashboard) have no program.coachOrgId
              if (program && program.coachOrgId && program.coachOrgId !== orgId) {
                console.log('Clue to err - program with diff orgId', program)
              } else {
                programsWithoutWrongOrgId[programId] = program
              }
            })

            api.updateCachedData(() => programsWithoutWrongOrgId)
          }
        })
      },
    }),
    createProgram: build.mutation({
      queryFn: async ({ orgId, programMeta, optionalProgId }) => {
        try {
          const { newProgram, newWorkouts } = getNewProgram(programMeta, optionalProgId)
          const programId = newProgram.id

          const { error: draftErr } = await updateFirebase(`drafts/${orgId}/programs/${programId}`, newProgram)

          const { error: wktsErr } = await updateFirebase(`drafts/${orgId}/workouts`, newWorkouts)

          const { error: publishedErr } = await updateFirebase(`programs/${programId}`, {
            ...newProgram,
            workouts: null, // program shouldn't have workouts until published
            coachOrgId: orgId, //needed for backend & other logic
          })

          if (draftErr || wktsErr || publishedErr) {
            throw draftErr || wktsErr || publishedErr
          }
        } catch (e) {
          console.error('createProgram error', e)
          return { error: e }
        } finally {
          return {
            status: 'success',
          }
        }
      },
    }),
    duplicateProgram: build.mutation({
      queryFn: async ({ orgId, program }, api) => {
        try {
          const state = api.getState()
          const multiWktDays = state.program.multiWktDays

          const { workouts } = await getProgramWorkouts({
            state,
            orgId,
            programId: program.id,
            wktIds: Object.keys(program.workouts || {}),
          })

          let nonMultiWktsToDuplicate = {}
          let multiWktIdsToMainWktIds = {}
          each(workouts, (wkt, wktId) => {
            if (wkt?.multiWktDayId) {
              const multiWktIds = multiWktDays?.[wkt.multiWktDayId]?.workouts || []
              multiWktIds.forEach((multiWktId) => {
                multiWktIdsToMainWktIds[multiWktId] = wktId
              })
            } else {
              nonMultiWktsToDuplicate[wktId] = wkt
            }
          })

          const { workouts: multiWkts } = await getProgramWorkouts({
            state,
            orgId,
            programId: program.id,
            wktIds: Object.keys(multiWktIdsToMainWktIds || {}),
          })

          const newProgramId = createUID()
          const { duplicatedWkts, updatedWktIdsToDayIdcs } = getDuplicatedProgramWkts({
            workouts: nonMultiWktsToDuplicate,
          })

          const { duplicatedMultiWkts, updatedMultiWktIdsToDayIdcs, updatedMultiWktDays } =
            getDuplicatedProgramMultiWkts({
              multiWkts,
              multiWktDays,
            })

          const duplicatedProgram = getDuplicatedProgram({
            newProgramId,
            program,
            updatedWktIdsToDayIdcs: { ...updatedWktIdsToDayIdcs, ...updatedMultiWktIdsToDayIdcs },
            updatedMultiWktDays,
          })

          const { error: draftErr } = await updateFirebase(
            `drafts/${orgId}/programs/${newProgramId}`,
            duplicatedProgram
          )

          // Add program contentTags
          const updateProgContentTagsErr = await updateItemContentTags({
            orgId,
            item: duplicatedProgram,
            contentId: newProgramId,
            contentType: 'program',
            updateType: 'add',
          })

          const { error: wktsErr } = await updateFirebase(`drafts/${orgId}/workouts`, {
            ...duplicatedWkts,
            ...duplicatedMultiWkts,
          })

          // Add workout contentTags
          const updateWktContentTagsErr = await updateManyWorkoutContentTags({
            orgId,
            workouts: { ...duplicatedWkts, ...duplicatedMultiWkts },
            programId: newProgramId,
            updateType: 'add',
          })

          const { error: publishedErr } = await updateFirebase(`programs/${newProgramId}`, {
            ...duplicatedProgram,
            multiWktDays: null, // program shouldn't have multiWktDays until published
            workouts: null, // program shouldn't have workouts until published
            coachOrgId: orgId, //needed for backend & other logic
          })

          if (
            draftErr ||
            wktsErr ||
            publishedErr ||
            updateWktContentTagsErr?.instrErr ||
            updateWktContentTagsErr?.equipmentErr ||
            updateWktContentTagsErr?.difficultyErr ||
            updateWktContentTagsErr?.tagsErr ||
            updateProgContentTagsErr.instrErr ||
            updateProgContentTagsErr.equipmentErr ||
            updateProgContentTagsErr.difficultyErr ||
            updateProgContentTagsErr.tagsErr
          ) {
            throw (
              draftErr ||
              wktsErr ||
              publishedErr ||
              updateWktContentTagsErr?.instrErr ||
              updateWktContentTagsErr?.equipmentErr ||
              updateWktContentTagsErr?.difficultyErr ||
              updateWktContentTagsErr?.tagsErr ||
              updateProgContentTagsErr?.instrErr ||
              updateProgContentTagsErr?.equipmentErr ||
              updateProgContentTagsErr?.difficultyErr ||
              updateProgContentTagsErr?.tagsErr
            )
          }
        } catch (e) {
          console.error('duplicateProgram error', e)
          return { error: e }
        } finally {
          return {
            status: 'success',
          }
        }
      },
    }),
    listenWorkout: build.query({
      queryFn: () => ({ data: { stillLoading: true } }),
      onCacheEntryAdded: async ({ orgId, workoutId }, api) => {
        const path = `drafts/${orgId}/workouts/${workoutId}`
        listenFirebase(path, api, (wkt) => {
          const firebaseWkt = { ...wkt, id: workoutId }
          const prepped = prepWorkout(firebaseWkt)

          api.updateCachedData(() => prepped)

          api.dispatch(
            draftWorkoutRetrieved({
              workoutId,
              workout: prepped,
            })
          )
        })
      },
    }),
    getAllProgramWorkouts: build.query({
      queryFn: async ({ orgId, programId, wktIds }, api) => {
        const { workouts: data } = await getProgramWorkouts({
          state: api.getState(),
          orgId,
          programId,
          wktIds: Object.keys(wktIds || {}),
        })
        return { data }
      },
    }),
    getWorkoutsByIds: build.query({
      queryFn: async ({ orgId, wktIds, prioritizeCopied = false }, api) => {
        const workouts = await getWorkoutsByIds({ state: api.getState(), orgId, wktIds, prioritizeCopied })
        return { data: workouts }
      },
    }),
    publishDraftProgram: build.mutation({
      // The workouts are read from the store rather than fetched separately, because fetching
      // them again would be slow and passing state.program.workouts down to ProgramView would
      // cause too many re-renders. queryFn gets the store from its api argument.
      queryFn: async ({ orgId, programId, wktIdsToDayIdcs, name, reviewedLowConfWkts }, { getState }) => {
        const state = getState()
        const multiWktDays = state.program.multiWktDays
        console.log(state.program)
        try {
          const { workouts, error: getWktsErr } = await getProgramWorkouts({
            state,
            orgId,
            programId,
            wktIds: Object.keys(wktIdsToDayIdcs || {}),
          })

          let multiWktIdsToMainWktIds = {}
          each(workouts, (wkt, wktId) => {
            if (wkt?.multiWktDayId) {
              const multiWktIds = multiWktDays?.[wkt.multiWktDayId]?.workouts || []
              multiWktIds.forEach((multiWktId) => {
                multiWktIdsToMainWktIds[multiWktId] = wktId
              })
            }
          })

          const { workouts: multiWkts, error: multiWktsErr } = await getProgramWorkouts({
            state,
            orgId,
            programId,
            wktIds: Object.keys(multiWktIdsToMainWktIds || {}),
          })

          if (getWktsErr) throw getWktsErr
          if (multiWktsErr) throw multiWktsErr

          const { data: orgExercises } = await getFirebase(`coachOrgs/${orgId}/exercises`)
          const { data: orgBenchmarks } = await getFirebase(`coachOrgs/${orgId}/benchmarks`)
          const { data: orgWktDurationInflationEnabled } = await getFirebase(
            `coachOrgs/${orgId}/mobilePreMountSettings/enableWktDurationInflation`
          )

          let cleanedWkts = cleanDraftProgramForPublishing(workouts)
          let draftWktsUpdate = {}
          Object.entries(cleanedWkts).forEach(([wktId, workout]) => {
            if (workout?.type === 'workout') {
              const inflationEnabled = getInflationEnabled({
                wktDurationInflationEnabled: workout?.enableWktDurationInflation,
                orgWktDurationInflationEnabled,
              })
              const { totalSeconds: durationSeconds } = calculateWorkoutEstimate({
                workout,
                orgExercises,
                orgBenchmarks,
                inflationEnabled,
              })

              const durationMS = durationSeconds * 1000
              const durationStr = getTimeDurationString(durationMS)
              if (workout?.durationEstOverride) {
                cleanedWkts[wktId].durationEstOverride = durationStr
                cleanedWkts[wktId].durationEst = null
                cleanedWkts[wktId].durationEstSource = null
                cleanedWkts[wktId].confidenceScore = null
                draftWktsUpdate[`${wktId}/durationEstSource`] = null
                draftWktsUpdate[`${wktId}/confidenceScore`] = null
              } else {
                const isLowConfidence = reviewedLowConfWkts?.[wktId] !== undefined
                const wasReviewed = reviewedLowConfWkts?.[wktId] === true
                // Under three minutes nothing is stored, whoever wrote the value there before:
                // members see no estimate for such a workout (MIN_WKT_DURATION_SECONDS).
                const tooShort = isTooShortToShow(durationSeconds)
                if (!tooShort && (!isLowConfidence || wasReviewed)) {
                  cleanedWkts[wktId].durationEst = durationStr
                  cleanedWkts[wktId].durationEstSource = null
                  cleanedWkts[wktId].confidenceScore = null
                  draftWktsUpdate[`${wktId}/durationEstSource`] = null
                  draftWktsUpdate[`${wktId}/confidenceScore`] = null
                } else if (tooShort || workout?.durationEstSource !== 'script') {
                  cleanedWkts[wktId].durationEst = null
                  cleanedWkts[wktId].durationEstSource = null
                  cleanedWkts[wktId].confidenceScore = null
                  draftWktsUpdate[`${wktId}/durationEstSource`] = null
                  draftWktsUpdate[`${wktId}/confidenceScore`] = null
                }
                cleanedWkts[wktId].durationEstOverride = null
              }
            }
          })

          let { cleanedWorkouts: cleanedMultiWkts, updatedMultiWktDays } = cleanMultiWktsForPublishing({
            multiWkts,
            multiWktDays,
          })

          Object.entries(cleanedMultiWkts).forEach(([wktId, workout]) => {
            if (workout?.type === 'workout') {
              const inflationEnabled = getInflationEnabled({
                wktDurationInflationEnabled: workout?.enableWktDurationInflation,
                orgWktDurationInflationEnabled,
              })
              const { totalSeconds: durationSeconds } = calculateWorkoutEstimate({
                workout,
                orgExercises,
                orgBenchmarks,
                inflationEnabled,
              })

              const durationMS = durationSeconds * 1000
              const durationStr = getTimeDurationString(durationMS)
              if (workout?.durationEstOverride) {
                cleanedMultiWkts[wktId].durationEstOverride = durationStr
                cleanedMultiWkts[wktId].durationEst = null
                cleanedMultiWkts[wktId].durationEstSource = null
                cleanedMultiWkts[wktId].confidenceScore = null
                draftWktsUpdate[`${wktId}/durationEstSource`] = null
                draftWktsUpdate[`${wktId}/confidenceScore`] = null
              } else {
                const isLowConfidence = reviewedLowConfWkts?.[wktId] !== undefined
                const wasReviewed = reviewedLowConfWkts?.[wktId] === true
                // Under three minutes nothing is stored, whoever wrote the value there before:
                // members see no estimate for such a workout (MIN_WKT_DURATION_SECONDS).
                const tooShort = isTooShortToShow(durationSeconds)
                if (!tooShort && (!isLowConfidence || wasReviewed)) {
                  cleanedMultiWkts[wktId].durationEst = durationStr
                  cleanedMultiWkts[wktId].durationEstSource = null
                  cleanedMultiWkts[wktId].confidenceScore = null
                  draftWktsUpdate[`${wktId}/durationEstSource`] = null
                  draftWktsUpdate[`${wktId}/confidenceScore`] = null
                } else if (tooShort || workout?.durationEstSource !== 'script') {
                  cleanedMultiWkts[wktId].durationEst = null
                  cleanedMultiWkts[wktId].durationEstSource = null
                  cleanedMultiWkts[wktId].confidenceScore = null
                  draftWktsUpdate[`${wktId}/durationEstSource`] = null
                  draftWktsUpdate[`${wktId}/confidenceScore`] = null
                }
                cleanedMultiWkts[wktId].durationEstOverride = null
              }
            }
          })

          const { data: defaultProgressionTags } = await getFirebase(
            `drafts/${orgId}/programs/${programId}/defaultProgressionTags`
          )

          const publishPath = `programs/${programId}`
          const { error: publishErr } = await updateFirebase(publishPath, {
            workoutsV2: { ...cleanedWkts, ...cleanedMultiWkts },
            multiWktDays: updatedMultiWktDays,
            ...(defaultProgressionTags ? { defaultProgressionTags } : {}),
          })
          if (publishErr) throw publishErr

          if (!isEmpty(draftWktsUpdate)) {
            const { error: draftWktsErr } = await updateFirebase(`drafts/${orgId}/workouts`, draftWktsUpdate)
            if (draftWktsErr) throw draftWktsErr
          }

          const orgPath = `coachOrgs/${orgId}/programs/${programId}`
          const { error: orgErr } = await setFirebase(orgPath, name || true)
          if (orgErr) throw orgErr

          return { data: 'success' }
        } catch (e) {
          console.error('publishDraftProgram error', e)
          return { error: e }
        }
      },
    }),
    addWeek: build.mutation({
      queryFn: async ({ orgId, programId, weekIdx }) => {
        console.log('API addWeek')
        const { newWorkouts, idToDayIdx } = getNewEmptyWeek(weekIdx)

        const wktPath = `drafts/${orgId}/workouts`
        const { error: updateWktsErr } = await updateFirebase(wktPath, newWorkouts)

        if (updateWktsErr) {
          console.error('addWeek updateWktsErr:', updateWktsErr)
          return
        }

        const progPath = `drafts/${orgId}/programs/${programId}/workouts`
        const { status, error: updateProgErr } = await updateFirebase(progPath, idToDayIdx)

        if (updateProgErr) {
          console.error('addWeek updateProgErr:', updateProgErr)
          return
        }

        return { status, error: { updateWktsErr, updateProgErr } }
      },
    }),
    deleteWeek: build.mutation({
      queryFn: async (
        { orgId, programId, weekIdx, weekWktIds, wktIdsSorted, multiWktDays },
        { dispatch, getState }
      ) => {
        let reorderedProgWkts = {}
        let firebaseWktsUpdate = {}
        let fbMultiWktDaysIdxUpdate = {}

        let wktIdToMultiWktIds = {}
        let wktIdToMultiWktDayId = {}
        each(multiWktDays || {}, (day, dayId) => {
          const wktIds = day?.workouts || []
          wktIds.forEach((wktId) => {
            wktIdToMultiWktIds[wktId] = wktIds
            wktIdToMultiWktDayId[wktId] = dayId
          })
        })

        const weekWorkouts = await getWorkoutsByIds({ state: getState(), orgId, wktIds: weekWktIds })
        let multiWktDayIds = []
        each(weekWorkouts, (wkt) => {
          if (wkt?.multiWktDayId) {
            multiWktDayIds.push(wkt.multiWktDayId)
          }
        })

        let multiWktIds = []
        let fbMultiWktDaysDelete = {}
        multiWktDayIds.forEach((dayId) => {
          if (multiWktDays?.[dayId]?.workouts) {
            fbMultiWktDaysDelete[dayId] = null
            const wktIds = multiWktDays?.[dayId]?.workouts || []
            multiWktIds = [...multiWktIds, ...wktIds]
          }
        })
        const multiWkts = await getWorkoutsByIds({ state: getState(), orgId, wktIds: multiWktIds })

        const updateContentTagsErr = await updateManyWorkoutContentTags({
          orgId,
          workouts: { ...weekWorkouts, ...multiWkts },
          programId,
          updateType: 'remove',
        })

        if (
          updateContentTagsErr?.instrErr ||
          updateContentTagsErr?.equipmentErr ||
          updateContentTagsErr?.difficultyErr ||
          updateContentTagsErr?.tagsErr
        ) {
          console.error('deleteWeek updateContentTagsErr:', updateContentTagsErr)
          return
        }

        const isLastRemainingWeek = wktIdsSorted.length === 7
        if (!isLastRemainingWeek) {
          each(wktIdsSorted, (wktId, dayIdx) => {
            if (weekWktIds.includes(wktId)) {
              reorderedProgWkts[wktId] = null
              firebaseWktsUpdate[wktId] = null
            }

            const afterCurrWeekRange = dayIdx + 1 > (weekIdx + 1) * 7
            if (afterCurrWeekRange) {
              const updatedDayIdx = dayIdx - 7
              reorderedProgWkts[wktId] = updatedDayIdx

              const fbKey = `${wktId}/dayIdx`
              firebaseWktsUpdate[fbKey] = updatedDayIdx

              const wktIds = wktIdToMultiWktIds?.[wktId]
              const isMultiWkt = wktIds?.length > 0
              if (isMultiWkt) {
                wktIds.forEach((id) => {
                  // update draft multi workout dayIdxs
                  const fbKey = `${id}/dayIdx`
                  firebaseWktsUpdate[fbKey] = updatedDayIdx
                })

                const dayId = wktIdToMultiWktDayId[wktId]
                if (fbMultiWktDaysDelete[dayId] !== null) {
                  // update multiWktDay dayIdx
                  const fbKey = `${dayId}/dayIdx`
                  fbMultiWktDaysIdxUpdate[fbKey] = updatedDayIdx
                }
              }
            }
          })
        } else {
          each(wktIdsSorted, (wktId, dayIdx) => {
            firebaseWktsUpdate[wktId] = getNewEmptyWkt(dayIdx, wktId)
          })
        }

        // delete multi workouts
        multiWktIds.forEach((wktId) => {
          if (!firebaseWktsUpdate[wktId]) {
            firebaseWktsUpdate[wktId] = null
          }
        })

        let updateErrors = {}
        const wktPath = `drafts/${orgId}/workouts`
        const { error: updateWktsErr } = await updateFirebase(wktPath, firebaseWktsUpdate)
        updateErrors = { ...updateErrors, updateWktsErr }

        // update program multiWktDays
        const fbMultiWktDaysUpdate = { ...fbMultiWktDaysDelete, ...fbMultiWktDaysIdxUpdate }
        if (!isEmpty(fbMultiWktDaysUpdate)) {
          const multiWktDaysPath = `drafts/${orgId}/programs/${programId}/multiWktDays`
          const { error: updateMultiWktDaysErr } = await updateFirebase(multiWktDaysPath, fbMultiWktDaysUpdate)

          updateErrors = { ...updateErrors, updateMultiWktDaysErr }
        }

        if (updateWktsErr) {
          console.error('deleteWeek updateWktsErr:', updateWktsErr)
          return
        }

        if (!isLastRemainingWeek) {
          const progPath = `drafts/${orgId}/programs/${programId}/workouts`
          const { error: updateProgErr } = await updateFirebase(progPath, reorderedProgWkts)
          updateErrors = { ...updateErrors, updateProgErr }

          if (updateProgErr) {
            console.error('deleteWeek updateProgErr:', updateProgErr)
            return
          }
        }

        dispatch(draftWorkoutsDeleted({ workoutIds: [...weekWktIds, ...multiWktIds] }))

        const state = getState()
        const isDeletingCopiedWeek = state.program.weeksCopied.find(
          (week) => week.programId === programId && week.weekIdx === weekIdx
        )

        if (isDeletingCopiedWeek) {
          dispatch(copiedWeeksCleared())
        }

        await updateTabRowsOnDelete({ coachOrgId: orgId, itemIds: [...weekWktIds, ...multiWktIds] })
        await removeItemsInCollections({ coachOrgId: orgId, itemIds: [...weekWktIds, ...multiWktIds] })

        return { error: updateErrors }
      },
    }),
    createWorkout: build.mutation({
      queryFn: async ({ orgId, programId, workoutId, workout, isMultiWkt = false }) => {
        console.log('API createWorkout')
        const newWkt = getNewWorkout(workout.dayIdx, workoutId)
        const wktPath = `drafts/${orgId}/workouts/${workoutId}`
        const { error: setWktErr } = await setFirebase(wktPath, {
          ...newWkt,
          multiWktIdx: workout?.multiWktIdx ?? null,
          multiWktDayId: workout?.multiWktDayId || null,
        })
        if (setWktErr) {
          console.error('createWorkout setWktErr:', setWktErr)
          return
        }

        if (!isMultiWkt) {
          const progPath = `drafts/${orgId}/programs/${programId}/workouts/${workoutId}`
          const { error: setProgWktErr } = await setFirebase(progPath, newWkt.dayIdx)
          if (setProgWktErr) {
            console.error('createWorkout setProgWktErr:', setProgWktErr)
            return
          }
        }
      },
    }),
    addExtraWorkoutToDay: build.mutation({
      queryFn: async ({ orgId, programId, dayIdx, multiWktDayId, mainWktId }) => {
        let multiWktDayIdToUse = multiWktDayId
        console.log('API addExtraWorkoutToDay')
        const progMultiWktsPath = `drafts/${orgId}/programs/${programId}/multiWktDays/${multiWktDayIdToUse}/workouts`
        const { data: progMultiWkts } = await getFirebase(progMultiWktsPath)
        const multiWkts = progMultiWkts || [mainWktId]

        const emptyWkt = getNewEmptyWkt(dayIdx)
        if (multiWkts.length === 1) {
          const wktPath = `drafts/${orgId}/workouts`
          multiWktDayIdToUse = createUID()
          await updateFirebase(wktPath, {
            [`${mainWktId}/multiWktIdx`]: 0,
            [`${mainWktId}/multiWktDayId`]: multiWktDayIdToUse,
            [emptyWkt.id]: {
              ...emptyWkt,
              multiWktIdx: multiWkts.length,
              multiWktDayId: multiWktDayIdToUse,
            },
          })
        } else {
          const wktPath = `drafts/${orgId}/workouts/${emptyWkt.id}`
          await setFirebase(wktPath, { ...emptyWkt, multiWktIdx: multiWkts.length, multiWktDayId: multiWktDayIdToUse })
        }

        const progPath = `drafts/${orgId}/programs/${programId}`
        const { error: setProgWktErr } = await updateFirebase(progPath, {
          [`multiWktDays/${multiWktDayIdToUse}/id`]: multiWktDayIdToUse,
          [`multiWktDays/${multiWktDayIdToUse}/dayIdx`]: dayIdx,
          [`multiWktDays/${multiWktDayIdToUse}/workouts`]: [...multiWkts, emptyWkt.id],
        })
        if (setProgWktErr) {
          console.error('addExtraWorkoutToDay setProgWktErr:', setProgWktErr)
          return
        }

        return { error: { setProgWktErr } }
      },
    }),
    updateMultiWktDays: build.mutation({
      queryFn: async ({ orgId, programId, update }) => {
        const progMultiWktDaysPath = `drafts/${orgId}/programs/${programId}/multiWktDays`
        const { data, error } = await updateFirebase(progMultiWktDaysPath, update)
        return { data, error }
      },
    }),
    updateMultiWktDaysInfo: build.mutation({
      queryFn: async ({ orgId, programId, multiWktDayId, update }) => {
        const progMultiWktsPath = `drafts/${orgId}/programs/${programId}/multiWktDays/${multiWktDayId}`
        const { data, error } = await updateFirebase(progMultiWktsPath, update)
        return { data, error }
      },
    }),
    updateProgram: build.mutation({
      queryFn: async ({ coachOrgId, programId, program }) => {
        console.log('API updateProgram, programId:', programId, 'program:', program)
        const draftPath = `/drafts/${coachOrgId}/programs/${programId}`
        const { error: draftErr } = await updateFirebase(draftPath, program)

        const publishedPath = `/programs/${programId}`
        const { error: publishedErr } = await updateFirebase(publishedPath, program)

        return {
          status: !draftErr && !publishedErr ? 'success' : 'error',
          error: { draftErr, publishedErr },
        }
      },
    }),
    removeProgram: build.mutation({
      queryFn: async ({ coachOrgId, programId }, { getState }) => {
        try {
          const progPath = `drafts/${coachOrgId}/programs/${programId}`
          const { data: program } = await getFirebase(progPath)
          const multiWktDays = program?.multiWktDays || {}
          const programWkts = program?.workouts
          const programWktIds = programWkts ? Object.keys(programWkts) : []
          const weekWorkouts = await getWorkoutsByIds({ state: getState(), orgId: coachOrgId, wktIds: programWktIds })

          let multiWktDayIds = []
          each(weekWorkouts, (wkt) => {
            if (wkt?.multiWktDayId) {
              multiWktDayIds.push(wkt.multiWktDayId)
            }
          })

          let multiWktIds = []
          multiWktDayIds.forEach((dayId) => {
            if (multiWktDays?.[dayId]?.workouts) {
              const wktIds = multiWktDays?.[dayId]?.workouts || []
              multiWktIds = [...multiWktIds, ...wktIds]
            }
          })
          const multiWkts = await getWorkoutsByIds({ state: getState(), orgId: coachOrgId, wktIds: multiWktIds })

          // Delete program contentTags
          await updateItemContentTags({
            orgId: coachOrgId,
            item: program,
            contentId: programId,
            contentType: 'program',
            updateType: 'remove',
          })
          // Delete workout contentTags
          await updateManyWorkoutContentTags({
            orgId: coachOrgId,
            workouts: { ...weekWorkouts, ...multiWkts },
            programId,
            updateType: 'remove',
          })
          // Delete draft workouts of program
          await deleteDraftWktsOfProgram({ coachOrgId, programWkts: { ...programWkts, ...multiWkts } })
          // Remove program and draft workouts from app layout draft, published and private rows
          await updateTabRowsOnDelete({ coachOrgId, itemIds: [programId, ...programWktIds, ...multiWktIds] })
          await removeItemsInCollections({ coachOrgId, itemIds: [programId, ...programWktIds, ...multiWktIds] })
          await removeProgramInProgramGroups({ coachOrgId, programId })
          await updateFirebase('/', {
            [`drafts/${coachOrgId}/programs/${programId}`]: null,
            [`coachOrgs/${coachOrgId}/programs/${programId}`]: null,
          })
          await removeFirebase(`programs/${programId}`)
        } catch (e) {
          console.error('removeProgram error', e)
          return { error: e }
        } finally {
          return { status: 'success' }
        }
      },
    }),
    updateWorkouts: build.mutation({
      queryFn: async ({ orgId, workouts }) => {
        const path = `drafts/${orgId}/workouts`
        const { data, error } = await updateFirebase(path, workouts)
        return { data, error }
      },
    }),
    updateWorkout: build.mutation({
      queryFn: async ({ orgId, workoutId, workout }) => {
        console.log('API updateWorkout. dayIdx:', workout.dayIdx)
        const path = `drafts/${orgId}/workouts/${workoutId}`

        const { data, error } = await updateFirebase(path, workout)
        return { data, error }
      },
    }),
    cleanWorkout: build.mutation({
      queryFn: async ({ orgId, workoutId, workout }) => {
        const cleanedWorkout = cleanWorkout({ ...workout, ...takePendingWorkoutText(workoutId) }, false)
        const path = `drafts/${orgId}/workouts/${workoutId}`

        const { data, error } = await updateFirebase(path, cleanedWorkout)
        return { data, error }
      },
    }),
    deleteWorkout: build.mutation({
      queryFn: async ({ orgId, programId, workoutId, workout, dayIdx }, { dispatch }) => {
        console.log('API deleteWorkout, dayIdx:', dayIdx)
        console.log('API deleteWorkout, old workoutId:', workoutId)

        dispatch(draftWorkoutsDeleted({ workoutIds: [workoutId] }))

        //create new empty day
        const emptyDay = getNewEmptyWkt(dayIdx)
        const newWktId = emptyDay.id

        //save new day to drafts/orgId/workouts
        const newWktPath = `drafts/${orgId}/workouts/${newWktId}`
        const { error: newWktErr } = await setFirebase(newWktPath, emptyDay)

        //update program in one call so ProgramView doesn't render with 6 days in a week
        const fbProgramUpdate = {
          [workoutId]: null,
          [newWktId]: dayIdx,
        }

        const programWktsPath = `drafts/${orgId}/programs/${programId}/workouts`
        const { error: progWktsErr } = await updateFirebase(programWktsPath, fbProgramUpdate)

        //delete old draft workout
        const prevWktPath = `drafts/${orgId}/workouts/${workoutId}`
        const { error: prevWktErr } = await removeFirebase(prevWktPath)

        //delete workout content tags
        const { instrErr, equipmentErr, difficultyErr, tagsErr } = await updateItemContentTags({
          orgId,
          item: workout,
          contentId: workoutId,
          contentType: 'workout',
          programId,
          updateType: 'remove',
        })

        if (newWktErr || progWktsErr || prevWktErr || instrErr || equipmentErr || difficultyErr || tagsErr) {
          return {
            error: newWktErr || progWktsErr || prevWktErr || instrErr || equipmentErr || difficultyErr || tagsErr,
          }
        } else {
          return { status: 'success' }
        }
      },
    }),
    deleteMultiWorkout: build.mutation({
      queryFn: async ({ orgId, programId, dayIdx, multiWktDayId, workoutIds, mainWktId }, { getState, dispatch }) => {
        console.log('API deleteMultiWorkout, workoutIds:', workoutIds)

        dispatch(draftWorkoutsDeleted({ workoutIds }))

        //create new empty day
        const emptyDay = getNewEmptyWkt(dayIdx)
        const newWktId = emptyDay.id

        let fbProgramUpdate = {
          [`workouts/${mainWktId}`]: null,
          [`workouts/${newWktId}`]: dayIdx,
          [`multiWktDays/${multiWktDayId}`]: null,
        }

        const programWktsPath = `drafts/${orgId}/programs/${programId}`
        const { error: progWktsErr } = await updateFirebase(programWktsPath, fbProgramUpdate)

        // add new draft workout and delete old draft workouts
        const draftWktsPath = `drafts/${orgId}/workouts`
        let draftWktsUpdate = {
          [newWktId]: emptyDay,
        }
        workoutIds.forEach((wktId) => {
          draftWktsUpdate[wktId] = null
        })
        const { error: prevWktErr } = await updateFirebase(draftWktsPath, draftWktsUpdate)

        //delete workout content tags
        const workouts = await getWorkoutsByIds({ state: getState(), orgId, wktIds: workoutIds })
        await updateManyWorkoutContentTags({
          orgId,
          workouts,
          programId,
          updateType: 'remove',
        })
        await updateTabRowsOnDelete({ coachOrgId: orgId, itemIds: workoutIds })
        await removeItemsInCollections({ coachOrgId: orgId, itemIds: workoutIds })

        if (progWktsErr || prevWktErr) {
          return {
            error: progWktsErr || prevWktErr,
          }
        } else {
          return { status: 'success', data: { newWktId } }
        }
      },
    }),
    deleteWktOfMultiWorkout: build.mutation({
      queryFn: async ({ orgId, programId, workout, workoutIds, mainWktId }, { dispatch }) => {
        const dayIdx = workout?.dayIdx
        const multiWktDayId = workout?.multiWktDayId
        const workoutId = workout?.id
        console.log('API deleteWktOfMultiWorkout, old workoutId:', workoutId)

        dispatch(draftWorkoutsDeleted({ workoutIds: [workoutId] }))

        const updatedWorkouts = workoutIds.filter((wktId) => wktId !== workoutId)
        const firstWktId = updatedWorkouts?.[0]

        let workoutsUpdate = {}
        let fbProgramUpdate = {}
        const isDeletingMainWkt = mainWktId === workoutId
        if (isDeletingMainWkt) {
          fbProgramUpdate[`workouts/${mainWktId}`] = null
          fbProgramUpdate[`workouts/${firstWktId}`] = dayIdx
          workoutsUpdate[`${firstWktId}/dayIdx`] = dayIdx
        }

        if (updatedWorkouts.length > 1) {
          fbProgramUpdate[`multiWktDays/${multiWktDayId}/workouts`] = updatedWorkouts
        } else {
          fbProgramUpdate[`multiWktDays/${multiWktDayId}`] = null
        }

        const programWktsPath = `drafts/${orgId}/programs/${programId}`
        const { error: progWktsErr } = await updateFirebase(programWktsPath, fbProgramUpdate)

        if (updatedWorkouts.length > 1) {
          updatedWorkouts.forEach((wktId, idx) => {
            workoutsUpdate[`${wktId}/multiWktIdx`] = idx
          })
        } else {
          // if deleting last wkt, should remove multiWktIdx because workout is no longer multiWkt
          updatedWorkouts.forEach((wktId, idx) => {
            workoutsUpdate[`${wktId}/multiWktIdx`] = null
            workoutsUpdate[`${wktId}/multiWktDayId`] = null
          })
        }

        const wktsPath = `drafts/${orgId}/workouts`
        const { error: wktsErr } = await updateFirebase(wktsPath, workoutsUpdate)

        //delete old draft workout
        const prevWktPath = `drafts/${orgId}/workouts/${workoutId}`
        const { error: prevWktErr } = await removeFirebase(prevWktPath)

        //delete workout content tags
        const { instrErr, equipmentErr, difficultyErr, tagsErr } = await updateItemContentTags({
          orgId,
          item: workout,
          contentId: workoutId,
          contentType: 'workout',
          programId,
          updateType: 'remove',
        })
        await updateTabRowsOnDelete({ coachOrgId: orgId, itemIds: [workoutId] })
        await removeItemsInCollections({ coachOrgId: orgId, itemIds: [workoutId] })

        if (progWktsErr || wktsErr || prevWktErr || instrErr || equipmentErr || difficultyErr || tagsErr) {
          return {
            error: progWktsErr || wktsErr || prevWktErr || instrErr || equipmentErr || difficultyErr || tagsErr,
          }
        } else {
          return { data: { newMainWktId: isDeletingMainWkt ? firstWktId : null } }
        }
      },
    }),
    listenWorkoutExercises: build.query({
      queryFn: ({ orgId, workoutId, partIdx }) => {
        return { data: { isLoading: true } }
      },
      onCacheEntryAdded: async ({ orgId, workoutId, partIdx }, api) => {
        const path = `drafts/${orgId}/workouts/${workoutId}/parts/${partIdx}/exercises`
        listenFirebase(path, api)
      },
    }),
    setWorkoutExercise: build.mutation({
      queryFn: async ({ orgId, workoutId, partIdx, exIdx, exercise }) => {
        console.log('ProgramAPI setWorkoutExercise. exercise:', exercise)
        const path = `drafts/${orgId}/workouts/${workoutId}/parts/${partIdx}/exercises/${exIdx}`
        const { data, error } = await setFirebase(path, exercise)
        return { data, error }
      },
    }),
    updateWorkoutExercise: build.mutation({
      queryFn: async ({ orgId, workoutId, partIdx, exIdx, exercise }) => {
        console.log('ProgramAPI updateWorkoutExercise with', exercise)
        const path = `drafts/${orgId}/workouts/${workoutId}/parts/${partIdx}/exercises/${exIdx}`
        const { data, error } = await updateFirebase(path, exercise)
        return { data, error }
      },
    }),
    listenProgramDefaultProgressionTags: build.query({
      queryFn: () => {
        return { data: { isLoading: true } }
      },
      onCacheEntryAdded: async ({ orgId, programId }, api) => {
        await listenFirebase(`drafts/${orgId}/programs/${programId}/defaultProgressionTags`, api)
      },
    }),
    setProgramDefaultProgressionTag: build.mutation({
      queryFn: async ({ orgId, programId, exId, tag }) => {
        const path = `drafts/${orgId}/programs/${programId}/defaultProgressionTags/${exId}`
        const { data, error } = tag ? await setFirebase(path, tag) : await removeFirebase(path)
        return { data, error }
      },
    }),
    addPart: build.mutation({
      queryFn: async ({ orgId, workoutId, workout }) => {
        console.log('API addPart')
        const newPart = getNewPart()
        const newWkt = JSON.parse(JSON.stringify(workout))
        newWkt.parts.push(newPart)

        const path = `drafts/${orgId}/workouts/${workoutId}`
        const { data, error } = await updateFirebase(path, newWkt)
        return { data, error }
      },
    }),
    addRestPart: build.mutation({
      queryFn: async ({ orgId, workoutId, workout }) => {
        console.log('API addRestPart')
        const newPart = getNewRestPart()
        const newWkt = JSON.parse(JSON.stringify(workout))
        newWkt.parts.push(newPart)

        const path = `drafts/${orgId}/workouts/${workoutId}`
        const { data, error } = await updateFirebase(path, newWkt)
        return { data, error }
      },
    }),
    addRestDay: build.mutation({
      queryFn: async ({ orgId, workoutId, workout }) => {
        console.log('API addRestDay')

        const restDay = getNewRestDay(workout.dayIdx, workoutId)
        const path = `drafts/${orgId}/workouts/${workoutId}`
        const { data, error } = await updateFirebase(path, {
          ...restDay,
          multiWktIdx: workout?.multiWktIdx ?? null,
          multiWktDayId: workout?.multiWktDayId || null,
        })
        return { data, error }
      },
    }),
    addVideoDay: build.mutation({
      queryFn: async ({ orgId, workoutId, workout }) => {
        console.log('API addVideoDay')

        const videoDay = getNewVideoDay(workout.dayIdx, workoutId)
        const path = `drafts/${orgId}/workouts/${workoutId}`
        const { data, error } = await updateFirebase(path, {
          ...videoDay,
          multiWktIdx: workout?.multiWktIdx ?? null,
          multiWktDayId: workout?.multiWktDayId || null,
        })
        return { data, error }
      },
    }),
    reorderProgram: build.mutation({
      queryFn: async ({ orgId, programId, reorderedProgramWkts, fbUpdateWkts }) => {
        console.log('API reorderProgram with', reorderedProgramWkts)

        const progPath = `drafts/${orgId}/programs/${programId}/workouts`
        const { error: setProgramErr } = await setFirebase(progPath, reorderedProgramWkts)

        const wktsPath = `drafts/${orgId}/workouts`
        const { error: updateWktsErr } = await updateFirebase(wktsPath, fbUpdateWkts)

        return { status: 'success', error: { setProgramErr, updateWktsErr } }
      },
    }),
    updateProgramWktsOrder: build.mutation({
      queryFn: async ({ orgId, programId, fbUpdateProgWkts, fbUpdateWkts }) => {
        const progPath = `drafts/${orgId}/programs/${programId}/workouts`
        const { error: updateProgramWktsErr } = await updateFirebase(progPath, fbUpdateProgWkts)

        const wktsPath = `drafts/${orgId}/workouts`
        const { error: updateWktsErr } = await updateFirebase(wktsPath, fbUpdateWkts)

        return { status: 'success', error: { updateProgramWktsErr, updateWktsErr } }
      },
    }),
    savePartExercises: build.mutation({
      queryFn: async ({ orgId, workoutId, partIndex, exercises }) => {
        console.log('API savePartExercises')
        const path = `drafts/${orgId}/workouts/${workoutId}/parts/${partIndex}/exercises`
        const { data, error } = await setFirebase(path, exercises)
        return { status: data, error }
      },
    }),
    listenStandaloneWorkoutsId: build.query({
      queryFn: ({ coachOrgId }) => {
        return { data: false }
      },
      onCacheEntryAdded: async ({ coachOrgId }, api) => {
        listenFirebase(`coachOrgs/${coachOrgId}/soloWorkoutsProgramId`, api)
      },
    }),
    listenProgramResourceImg: build.query({
      queryFn: ({ coachOrgId, programId, resourceId }) => {
        return { data: false }
      },
      onCacheEntryAdded: async ({ coachOrgId, programId, resourceId }, api) => {
        listenFirebase(`drafts/${coachOrgId}/programs/${programId}/programResources/${resourceId}/previewImg`, api)
      },
    }),
    listenProgramImg: build.query({
      queryFn: ({ programId }) => {
        return { data: false }
      },
      onCacheEntryAdded: async ({ programId }, api) => {
        listenFirebase(`programs/${programId}/previewImg`, api)
      },
    }),
    listenProgramIntroVideo: build.query({
      queryFn: ({ programId }) => {
        return { data: false }
      },
      onCacheEntryAdded: async ({ programId }, api) => {
        listenFirebase(`programs/${programId}/introVideo`, api)
      },
    }),
    listenDraftWorkoutImg: build.query({
      queryFn: ({ coachOrgId, workoutId }) => {
        return { data: false }
      },
      onCacheEntryAdded: async ({ coachOrgId, workoutId }, api) => {
        listenFirebase(`drafts/${coachOrgId}/workouts/${workoutId}/previewImg`, api)
      },
    }),
    updatePart: build.mutation({
      queryFn: async ({ orgId, workoutId, partIdx, part }) => {
        console.log('API updatePart')
        const path = `drafts/${orgId}/workouts/${workoutId}/parts/${partIdx}`
        const { data, error } = await updateFirebase(path, part)
        return { status: data, error }
      },
    }),
    updateParts: build.mutation({
      queryFn: async ({ orgId, workoutId, partsUpdate }) => {
        console.log('API updateParts')
        const path = `drafts/${orgId}/workouts/${workoutId}/parts`
        const { data, error } = await updateFirebase(path, partsUpdate)
        return { status: data, error }
      },
    }),
    saveParts: build.mutation({
      queryFn: async ({ orgId, workoutId, parts }) => {
        console.log('API saveParts')
        const path = `drafts/${orgId}/workouts/${workoutId}/parts`
        const { data, error } = await setFirebase(path, parts)
        return { status: data, error }
      },
    }),
    updateWorkoutMetadata: build.mutation({
      queryFn: async ({ coachOrgId, workoutId, metadata }) => {
        console.log('API updateWorkoutMetadata')
        const { data, error } = await updateFirebase(`drafts/${coachOrgId}/workouts/${workoutId}`, metadata)
        return { status: data, error }
      },
    }),
    setDraftProgramUpdatedAt: build.mutation({
      queryFn: async ({ coachOrgId, programId, timestamp }) => {
        const path = `drafts/${coachOrgId}/programs/${programId}/updatedAt`
        const { data, error } = await setFirebase(path, timestamp)
        return { status: data, error }
      },
    }),
    listenDraftProgramUpdatedAt: build.query({
      queryFn: () => {
        return { data: { isFiller: true } }
      },
      onCacheEntryAdded: async ({ orgId, programId }, api) => {
        const path = `drafts/${orgId}/programs/${programId}/updatedAt`
        listenFirebase(path, api)
      },
    }),
    listenSavedBlocks: build.query({
      queryFn: () => ({ data: { isFiller: true } }),
      onCacheEntryAdded: async ({ orgId }, api) => {
        const path = `drafts/${orgId}/savedBlocks`
        listenFirebase(path, api)
      },
    }),
    saveBlock: build.mutation({
      queryFn: async ({ orgId, part }) => {
        const savedBlocksPath = `drafts/${orgId}/savedBlocks`
        const { partId, partData } = changeSavedPartIds(part)
        const { data: savedBlocks } = await getFirebase(savedBlocksPath)

        if (!savedBlocks || Object.keys(savedBlocks).length <= 100) {
          const { data, error } = await setFirebase(`${savedBlocksPath}/${partId}`, partData)
          return { status: data, error }
        } else {
          return { error: { message: 'You hit the limit. 100 saved blocks at max' } }
        }
      },
    }),
    removeSavedBlock: build.mutation({
      queryFn: async ({ orgId, blockId }) => {
        const path = `drafts/${orgId}/savedBlocks/${blockId}`
        const { data, error } = await removeFirebase(path)
        return { status: data, error }
      },
    }),
    listenBenchmarks: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ orgId }, api) => {
        const path = `coachOrgs/${orgId}/benchmarks`
        listenFirebase(path, api)
      },
    }),
    listenBenchmark: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ orgId, benchmarkId }, api) => {
        const path = `coachOrgs/${orgId}/benchmarks/${benchmarkId}`
        listenFirebase(path, api)
      },
    }),
    updateBenchmark: build.mutation({
      queryFn: async ({ orgId, benchmarkId, benchmark }) => {
        const path = `coachOrgs/${orgId}/benchmarks/${benchmarkId}`
        const { data, error } = await updateFirebase(path, benchmark)
        return { status: data, error }
      },
    }),
    archiveBenchmark: build.mutation({
      queryFn: async ({ orgId, benchmarkId }) => {
        const path = `coachOrgs/${orgId}/benchmarks/${benchmarkId}/isArchived`
        const { data, error } = await setFirebase(path, true)
        return { status: data, error }
      },
    }),
  }),
  overrideExisting: false,
})

export const {
  useListenOrgProgramsListQuery,
  useListenDraftProgramQuery,
  useInitDraftProgramMutation,
  useCreateProgramMutation,
  useDuplicateProgramMutation,
  useFetchDraftProgramQuery,
  useListenWorkoutQuery,
  useGetAllProgramWorkoutsQuery,
  useGetWorkoutsByIdsQuery,
  useLazyGetWorkoutsByIdsQuery,
  useListenWorkoutExercisesQuery,
  usePublishDraftProgramMutation,
  useUpdateProgramMutation,
  useRemoveProgramMutation,
  useCreateWorkoutMutation,
  useAddExtraWorkoutToDayMutation,
  useUpdateMultiWktDaysMutation,
  useUpdateMultiWktDaysInfoMutation,
  useUpdateWorkoutsMutation,
  useUpdateWorkoutMutation,
  useCleanWorkoutMutation,
  useDeleteWorkoutMutation,
  useDeleteMultiWorkoutMutation,
  useDeleteWktOfMultiWorkoutMutation,
  useSetWorkoutExerciseMutation,
  useUpdateWorkoutExerciseMutation,
  useAddWeekMutation,
  useDeleteWeekMutation,
  useAddPartMutation,
  useAddRestPartMutation,
  useAddRestDayMutation,
  useAddVideoDayMutation,
  useCopyPasteWorkoutsMutation,
  useSavePartExercisesMutation,
  useUpdatePartMutation,
  useUpdatePartsMutation,
  useSavePartsMutation,
  useReorderProgramMutation,
  useUpdateProgramWktsOrderMutation,
  useListenStandaloneWorkoutsIdQuery,
  useListenProgramResourceImgQuery,
  useListenProgramImgQuery,
  useListenProgramIntroVideoQuery,
  useListenDraftWorkoutImgQuery,
  useUpdateWorkoutMetadataMutation,
  useSetDraftProgramUpdatedAtMutation,
  useListenDraftProgramUpdatedAtQuery,
  useListenSavedBlocksQuery,
  useSaveBlockMutation,
  useRemoveSavedBlockMutation,
  useListenBenchmarksQuery,
  useListenBenchmarkQuery,
  useUpdateBenchmarkMutation,
  useArchiveBenchmarkMutation,
  useListenProgramDefaultProgressionTagsQuery,
  useSetProgramDefaultProgressionTagMutation,
} = programApi
